/**
 * お題・追い質問の音声（課題 #160。2026-10-09 高尾さん決定）。
 *
 * - お題のカードが出たときに自動で流す（追い質問も、足したときに流す）
 * - 流している間だけ録音を一時停止し、流し終えたら（止まった・流せなかったときも）続きから録る。
 *   流した声が解析に混ざらないようにするため。一時停止の間は録音の長さに数えない
 * - 音のオン・オフは端末ごとに覚える（初期値はオン）
 * - 「聞いたか」は分からないので、「どう流れたか」を録音と一緒に送る（#60 の分析で使う）
 *
 * 画面の部品から切り離した決まりごとだけをここに置く（テストで確かめるため）。
 */
import type { TopicAudioOutcome } from '../api/client';

export type { TopicAudioOutcome };

/** 流れ方を残す枠。お題と追い質問で 1 つずつ（追い質問は 1 つのお題に 1 つだけ） */
export type TopicAudioSlot = 'question' | 'followUp';

export type TopicAudioOutcomes = Partial<Record<TopicAudioSlot, TopicAudioOutcome>>;

/**
 * 流す前に決まる流れ方。null は「流してみる」。
 * - 音声の URL が無い → no_audio（文字だけで出す）
 * - 音をオフにしている → muted
 * - 録音を一時停止できない端末 → failed（流すと声が録音に混ざるため、流さない）
 */
export function outcomeBeforePlaying(
  url: string | null | undefined,
  muted: boolean,
  canPauseRecording: boolean
): TopicAudioOutcome | null {
  if (!url) return 'no_audio';
  if (muted) return 'muted';
  if (!canPauseRecording) return 'failed';
  return null;
}

/**
 * 録音の送信に添える値。出さなかったお題・追い質問の分は送らない（BE の決まり）。
 * 出したかどうかは、流れ方が残っているかで見る（出したときは必ずどれかの値が残る）
 */
export function voiceJobAudioFields(outcomes: TopicAudioOutcomes): {
  topic_audio?: TopicAudioOutcome;
  follow_up_audio?: TopicAudioOutcome;
} {
  return {
    ...(outcomes.question ? { topic_audio: outcomes.question } : {}),
    ...(outcomes.followUp ? { follow_up_audio: outcomes.followUp } : {}),
  };
}

/** 音のオン・オフを覚えておくキー（端末ごと） */
export const TOPIC_AUDIO_MUTED_KEY = 'ceeu.topicAudioMuted';

/** 保存領域が使えないとき（プライベートブラウズなど）は、初期値のオンとして扱う */
export function readTopicAudioMuted(storage: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return storage?.getItem(TOPIC_AUDIO_MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeTopicAudioMuted(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  muted: boolean
): void {
  try {
    if (muted) storage?.setItem(TOPIC_AUDIO_MUTED_KEY, '1');
    else storage?.removeItem(TOPIC_AUDIO_MUTED_KEY);
  } catch {
    /* 覚えられなくても、この画面の間は切り替えが効く */
  }
}

/**
 * 実際に録っていた時間(ms)。一時停止していた時間を数えない。
 * 画面の秒数・60 秒で止める判定・20 秒以上で送信できる判定・BE に送る長さは、すべてこれで数える
 */
export function recordedMs(
  startedAt: number,
  pausedTotalMs: number,
  pausedSince: number | null,
  now: number
): number {
  const pausing = pausedSince != null ? Math.max(0, now - pausedSince) : 0;
  return Math.max(0, now - startedAt - pausedTotalMs - pausing);
}
